import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, isColumnTypeNumerical} from 'data-frames/DataFrameTypes';
import {getColumnMax, getColumnMin, getNumberColumnValues} from 'data-frames/util/ColumnSummaries';
import transformColumn from 'data-frames/util/transformColumn';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {
  isLeafTypeNumerical,
  LeafType,
  NUMERICAL_LEAF_TYPES,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  AnalyticsVisualizationColorPalette as ColorPalette,
  AnalyticsVisualizationAxisScale as AxisScale,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {INSIGHTS_SCALE_TYPE_TO_CHARTING_SCALE_TYPE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {getRegressionTypeFromConfig} from 'insights/analytics-visualizations/data/getRegressionType';
import {
  ScatterPlotConfig,
  ScatterPlotData,
  ScatterPlotProps,
  SCATTER_PLOT_TYPE_VALIDATION_MAP,
  SCATTER_PLOT_X_AXIS_ALLOWED_LEAF_TYPES,
} from 'ui-library/charting/ScatterPlot';
import {REGRESSION_TYPE_TO_READABLE} from 'ui-library/charting/statistics/regression/Constants';
import {RegressionType} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {CartesianPoint} from 'ui-library/charting/Types';
import {omit} from 'util/omitKey';
import {JSONValue} from 'util/Types';

import cleanAndValidateDataFrame, {makeInvalidColumnTypeErrorMessage} from './cleanAndValidateDataFrame';
import convertDataFrameToLegacyChartData from './convertDataFrameToLegacyChartData';
import ConvertToChartPropsException from './ConvertToChartPropsException';
import {transformRowObjectsToDatasets} from './legacy-prop-converters/runQueryOutputToChartData';
import maybeConvertProps from './maybeConvertProps';
import {ColumnInfo, ConversionOutput} from './PropConverterTypes';
import findAxisColumn from './utils/findAxisColumn';

type ScatterPlotParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

export type InsightsScatterPlotPoint = CartesianPoint & {
  id?: JSONValue;
  [SystemColumnName.ROW_ID]: JSONValue;
};

export interface ValidScatterPlotConversionOutput {
  valid: true;
  props: ScatterPlotProps<InsightsScatterPlotPoint>;
}

type InsightsScatterPlotData = ScatterPlotData<InsightsScatterPlotPoint>;

export const ERROR_MESSAGES = {
  LOG_AXIS_CROSSES_ZERO: 'Log scale can only be used when all values are either positive or negative.',
  LOG_AXIS_CONTAINS_ZERO: 'Log scale can’t be used when values include 0.',
  LOG_AXIS_INCLUDES_ZERO: 'Log scale can’t be used when axis includes 0.',
};

export const REGRESSION_TYPE_TO_AXIS_TYPES: {[key in RegressionType]?: Array<LeafType>} = {
  [RegressionType.LOGISTIC]: NUMERICAL_LEAF_TYPES,
};

export const ROW_ID_COLUMN_VALIDATION_OPTIONS = {
  allowedLeafTypes: [LeafType.JSON],
  nullableType: ColumnTypeValidationFlag.ALLOWED,
  listType: ColumnTypeValidationFlag.ALLOWED,
  maybeTextType: ColumnTypeValidationFlag.ALLOWED,
};

export function getScatterPlotColumnInfos(parameters: ScatterPlotParameters): Array<ColumnInfo> {
  const regressionType = getRegressionTypeFromConfig(parameters.regressionConfig);
  return [
    {
      chartDataColumnName: 'x',
      inputDataColumnSystemName: parameters.xAxisColumnName,
      typeValidationOptions: {
        ...SCATTER_PLOT_TYPE_VALIDATION_MAP.x,
        allowedLeafTypes:
          regressionType != null
            ? (REGRESSION_TYPE_TO_AXIS_TYPES[regressionType] ?? SCATTER_PLOT_X_AXIS_ALLOWED_LEAF_TYPES)
            : SCATTER_PLOT_X_AXIS_ALLOWED_LEAF_TYPES,
      },
      cleanupNulls: true,
    },
    {
      chartDataColumnName: 'y',
      inputDataColumnSystemName: parameters.yAxisColumnName,
      typeValidationOptions: SCATTER_PLOT_TYPE_VALIDATION_MAP.y,
      cleanupNulls: true,
    },
    ...(parameters.seriesColumnName == null
      ? []
      : [
          {
            chartDataColumnName: 'series',
            inputDataColumnSystemName: parameters.seriesColumnName,
            typeValidationOptions: SCATTER_PLOT_TYPE_VALIDATION_MAP.series,
            cleanupNulls: true,
          },
        ]),
    ...(parameters.rowIdColumnName == null
      ? []
      : [
          {
            chartDataColumnName: 'id',
            inputDataColumnSystemName: parameters.rowIdColumnName,
            typeValidationOptions: ROW_ID_COLUMN_VALIDATION_OPTIONS,
            cleanupNulls: false,
          },
        ]),
  ];
}

export function getExtraScatterPlotVisualizationDetails(
  parameters: ScatterPlotParameters
): string | undefined {
  const regressionType = getRegressionTypeFromConfig(parameters.regressionConfig);
  return regressionType != null
    ? `with ${REGRESSION_TYPE_TO_READABLE[regressionType].toLowerCase()} regressions`
    : undefined;
}

export function validateAxisType(
  dataFrame: DataFrame,
  dataFrameColumn: string,
  axisName: string,
  axisType: AxisScale
): void {
  if (axisType !== AxisScale.LOG) {
    return;
  }

  const column = findAxisColumn(dataFrame, dataFrameColumn);

  if (axisType === AxisScale.LOG) {
    if (!isColumnTypeNumerical(column.type)) {
      throw new ConvertToChartPropsException(
        makeInvalidColumnTypeErrorMessage(
          axisName.toUpperCase(),
          column.displayName,
          NUMERICAL_LEAF_TYPES,
          VisualizationType.SCATTER_PLOT,
          `configured with a logarithmic ${axisName}-axis scale`
        )
      );
    }

    if (getNumberColumnValues(dataFrame, column.systemName).some((value) => value === 0)) {
      throw new ConvertToChartPropsException(ERROR_MESSAGES.LOG_AXIS_CONTAINS_ZERO);
    }

    if (getColumnMin(dataFrame, column.systemName) * getColumnMax(dataFrame, column.systemName) < 0) {
      throw new ConvertToChartPropsException(ERROR_MESSAGES.LOG_AXIS_CROSSES_ZERO);
    }
  }
}

export function validateAxisTypes(
  dataFrame: DataFrame,
  {xAxisColumnName, xAxisScale, yAxisColumnName, yAxisIncludesZero, yAxisScale}: ScatterPlotParameters
): void {
  if (yAxisScale === AxisScale.LOG && yAxisIncludesZero) {
    throw new ConvertToChartPropsException(ERROR_MESSAGES.LOG_AXIS_INCLUDES_ZERO);
  }

  validateAxisType(dataFrame, xAxisColumnName, 'x', xAxisScale);
  validateAxisType(dataFrame, yAxisColumnName, 'y', yAxisScale);
}

export function validateColorPalette(
  dataFrame: DataFrame,
  {colorPalette, seriesColumnName}: ScatterPlotParameters
): void {
  if (seriesColumnName && colorPalette === ColorPalette.GRADIENT) {
    const seriesColumn = findAxisColumn(dataFrame, seriesColumnName);

    if (!isLeafTypeNumerical(seriesColumn.type.leafType)) {
      throw new ConvertToChartPropsException(
        `Gradient color palette requires a numerical series, but ${seriesColumn.displayName} is not numerical.`
      );
    }
  }
}

export function validateRowIDColumn(dataFrame: DataFrame, {rowIdColumnName}: ScatterPlotParameters): void {
  if (rowIdColumnName == null) {
    return;
  }

  const rowIdColumn = findAxisColumn(dataFrame, rowIdColumnName);
  const ids = dataFrame.rows.map((row) => String(row[rowIdColumn.systemName]));

  if (_.unique(ids).length !== ids.length) {
    throw new ConvertToChartPropsException(
      `Unique ID Field ${rowIdColumn.displayName} contains duplicate values. All values must be unique.`
    );
  }
}

export function updateRowIDSystemColumn(
  dataFrame: DataFrame,
  {rowIdColumnName}: ScatterPlotParameters
): DataFrame {
  if (rowIdColumnName == null) {
    return dataFrame;
  }
  const rowIdColumn = findAxisColumn(dataFrame, rowIdColumnName);

  return transformColumn(dataFrame, SystemColumnName.ROW_ID, (row) => String(row[rowIdColumn.systemName]));
}

export function cleanAndValidateScatterPlotData(
  dataFrame: DataFrame,
  parameters: ScatterPlotParameters
): DataFrame {
  const columnInfos = getScatterPlotColumnInfos(parameters);
  const extraVisualizationDetails = getExtraScatterPlotVisualizationDetails(parameters);

  const cleanDataFrame = cleanAndValidateDataFrame(
    dataFrame,
    columnInfos,
    VisualizationType.SCATTER_PLOT,
    extraVisualizationDetails
  );

  validateAxisTypes(cleanDataFrame, parameters);
  validateColorPalette(cleanDataFrame, parameters);
  validateRowIDColumn(cleanDataFrame, parameters);

  return updateRowIDSystemColumn(cleanDataFrame, parameters);
}

/**
 * @deprecated TODO(BNCH-24546): Delete convertScatterPlotData and use cleanAndValidateScatterPlotData directly
 * in convertScatterPlotProps once scatter plot takes a DataFrame.
 */
function convertScatterPlotData(
  dataFrame: DataFrame,
  parameters: ScatterPlotParameters
): InsightsScatterPlotData {
  const cleanDataFrame = cleanAndValidateScatterPlotData(dataFrame, parameters);

  const columnInfos = getScatterPlotColumnInfos(parameters);
  const legacyDataFrame = convertDataFrameToLegacyChartData(cleanDataFrame, columnInfos);

  const partitionedLegacyRows = transformRowObjectsToDatasets(
    legacyDataFrame.points,
    parameters.seriesColumnName ? 'series' : null
  );

  return {
    axisTypes: omit(legacyDataFrame.axisTypes, 'series'),
    datasets: partitionedLegacyRows,
  } as unknown as InsightsScatterPlotData;
}

export function convertScatterPlotConfig(
  dataFrame: DataFrame,
  parameters: ScatterPlotParameters
): ScatterPlotConfig {
  assertAndContinue(
    getRegressionTypeFromConfig(parameters.regressionConfig) == null || parameters.seriesColumnName == null,
    'Expected either regressionType or seriesColumnName to be null, both are non-null'
  );
  assertAndContinue(
    parameters.outlierConfig == null ||
      getRegressionTypeFromConfig(parameters.regressionConfig) === RegressionType.LOGISTIC,
    'Expected regressionType to be logistic when outlierConfig is non-null'
  );

  const xAxisLabel = findAxisColumn(dataFrame, parameters.xAxisColumnName).displayName;
  const yAxisLabel = findAxisColumn(dataFrame, parameters.yAxisColumnName).displayName;
  const seriesLabel = parameters.seriesColumnName
    ? findAxisColumn(dataFrame, parameters.seriesColumnName).displayName
    : '';

  return {
    colorPalette: parameters.colorPalette,
    xAxis: {
      includeZero: false,
      label: xAxisLabel,
      scale: INSIGHTS_SCALE_TYPE_TO_CHARTING_SCALE_TYPE[parameters.xAxisScale],
    },
    yAxis: {
      includeZero: parameters.yAxisIncludesZero,
      label: yAxisLabel,
      scale: INSIGHTS_SCALE_TYPE_TO_CHARTING_SCALE_TYPE[parameters.yAxisScale],
      ...(parameters.yAxisMin != null && {min: parameters.yAxisMin}),
      ...(parameters.yAxisMax != null && {max: parameters.yAxisMax}),
    },
    ...(parameters.legendLocation != null
      ? {
          legend: {
            label: seriesLabel,
          },
        }
      : {}),
  };
}

export function convertScatterPlotProps(
  dataFrame: DataFrame,
  parameters: ScatterPlotParameters
): ValidScatterPlotConversionOutput {
  return {
    valid: true,
    props: {
      config: convertScatterPlotConfig(dataFrame, parameters),
      data: convertScatterPlotData(dataFrame, parameters),
    },
  };
}

export default function maybeConvertScatterPlotProps(
  dataFrame: DataFrame,
  parameters: ScatterPlotParameters
): ConversionOutput<ScatterPlotProps<InsightsScatterPlotPoint>> {
  return maybeConvertProps(() => convertScatterPlotProps(dataFrame, parameters));
}
